<template>
  <v-dialog v-model="showDialog" max-width="640" persistent>
    <v-card>
      <v-card-title class="d-flex align-center">
        <span>{{ reconnectLabel }} {{ serverName }}</span>
        <v-spacer />
        <v-btn icon variant="text" @click="close"><v-icon>mdi-close</v-icon></v-btn>
      </v-card-title>
      <v-card-text>
        <v-alert v-if="errorMessage" type="error" density="compact" class="mb-3">{{ errorMessage }}</v-alert>
        <p class="mb-4">Complete the OAuth flow in the popup window. This dialog will keep polling as a fallback until the server reaches a terminal auth state.</p>
        <v-btn color="primary" :loading="loading" @click="start">{{ reconnectLabel }}</v-btn>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script lang="ts">
import { isApiError } from '@/types/api'
import { StartExternalMcpOAuthResponse, UserVisibleMcpServer } from '@/types/mcp'
import { Options, Vue } from 'vue-class-component'

@Options({
  props: {
    show: Boolean,
    serverName: String
  },
  emits: ['close', 'connected']
})
export default class ExternalMcpOAuthDialog extends Vue {
  show!: boolean
  serverName!: string
  loading = false
  errorMessage = ''
  popup: Window | null = null
  pollTimer: number | null = null
  pollStartedAt = 0
  expectedOrigin = ''
  private boundMessageHandler = (event: MessageEvent) => this.handlePopupMessage(event)

  get showDialog(): boolean {
    return this.show
  }

  get reconnectLabel(): string {
    return 'Connect OAuth'
  }

  async start(): Promise<void> {
    this.loading = true
    this.errorMessage = ''

    try {
      const result = (await this.$store.dispatch('startExternalServerOAuth', {
        serverName: this.serverName,
        body: {}
      })) as StartExternalMcpOAuthResponse

      this.expectedOrigin = new URL(result.callbackUrl).origin
      window.addEventListener('message', this.boundMessageHandler)
      this.popup = window.open(result.authorizationUrl, `external-mcp-oauth-${this.serverName}`, 'width=720,height=820')
      this.pollStartedAt = Date.now()
      this.pollServerState()
    } catch (error) {
      this.errorMessage = isApiError(error) ? error.message : error instanceof Error ? error.message : 'Failed to start OAuth'
      this.loading = false
    }
  }

  async handlePopupMessage(event: MessageEvent): Promise<void> {
    if (event.origin !== this.expectedOrigin) return
    if (event.data?.type !== 'external-mcp-oauth-complete') return
    if (event.data?.serverName !== this.serverName) return

    if (event.data?.success === true) {
      await this.pollUntilTerminalState()
      return
    }

    this.errorMessage = event.data?.error || 'OAuth did not complete. Retry connection.'
    this.stopPolling()
    this.loading = false
  }

  async pollServerState(): Promise<void> {
    if (this.pollTimer) {
      window.clearInterval(this.pollTimer)
    }

    this.pollTimer = window.setInterval(async () => {
      await this.pollUntilTerminalState()
    }, 2000)
  }

  async pollUntilTerminalState(): Promise<void> {
    const server = (await this.$store.dispatch('fetchUserServer', this.serverName)) as UserVisibleMcpServer | null
    if (!server) {
      return
    }

    if (server.authState === 'connected') {
      try {
        await this.$store.dispatch('refreshExternalServer', this.serverName)
      } catch {
        // Best effort only. Parent refresh path will run after success emit.
      }

      try {
        await this.$store.dispatch('fetchMcpTools', this.serverName)
      } catch {
        // Best effort only. Parent selection flow will retry if needed.
      }

      this.stopPolling()
      this.loading = false
      this.$emit('connected', this.serverName)
      this.$emit('close')
      return
    }

    if (server.authState === 'error' || server.authState === 'reauth_required') {
      this.errorMessage = server.statusMessage || 'OAuth did not complete. Retry connection.'
      this.stopPolling()
      this.loading = false
      return
    }

    if (Date.now() - this.pollStartedAt > 120000) {
      this.errorMessage = 'OAuth did not complete. Retry connection.'
      this.stopPolling()
      this.loading = false
    }
  }

  stopPolling() {
    if (this.pollTimer) {
      window.clearInterval(this.pollTimer)
      this.pollTimer = null
    }
    window.removeEventListener('message', this.boundMessageHandler)
  }

  close() {
    this.stopPolling()
    this.loading = false
    this.$emit('close')
  }

  beforeUnmount() {
    this.stopPolling()
  }
}
</script>
